import {
  createAction,
  createAsyncThunk,
  createSelector,
  createSlice
} from '@reduxjs/toolkit'
import { pathOr } from 'ramda'
import { RootState } from 'store'

import { emptyEditedProduct } from 'constants/productsStateDefaults'
import { IPagination } from 'types/assets'
import {
  IAutoTranslateProductResponse,
  IAutoTranslateSingleProductPayload,
  IBatchProgress,
  IBatchTranslateRequest,
  IBatchTranslateResponse,
  ICopyProductsPayload,
  ICopySingleProductPayload,
  IEditProduct,
  IEditProductPayload,
  IGenerateMultipleProductPackagePayload,
  IGenerateSingleProductPackagePayload,
  IProduct,
  IProductQueryPayload} from 'types/products'
import { IProductsState } from 'types/slices'
import { LoadingStatus, TLoadingStatus } from 'types/user'
import {
  groupProductAssetsByAssetType,
  mergeGroupedProductAssets
} from 'utils/assetsUtils'
import { handleApiErrors } from 'utils/errors'
import { transformObjectKeys } from 'utils/strings'
import { executeWithTransientRetries } from 'utils/transientHttpRetry'

import ProductsRepository from './repositories/ProductsRepository'

const initialState: IProductsState = {
  status: 'initial',
  listStatus: 'initial',
  products: [],
  editedProduct: emptyEditedProduct,
  pagination: {
    page: 0,
    pagesTotal: 0,
    recordsTotal: 0,
    take: 0
  },
  product: null,
  cachedProducts: {} // Initialize empty cache with correct type
}

export const types = {
  getProducts: 'products/getProducts',
  getProduct: 'products/getProduct',
  createProduct: 'products/createProduct',
  editProduct: 'products/editProduct',
  deleteProduct: 'products/deleteProduct',
  generateSingleProductPackage: 'products/generateSingleProductPackage',
  generateSingleProductIframe: 'products/generateSingleProductIframe',
  generateMultipleProductPackage: 'products/generateMultipleProductPackage',
  copyProducts: 'products/copyProducts',
  copySingleProduct: 'products/copySingleProduct',
  autoTranslateSingleProduct: 'products/autoTranslateSingleProduct',
  cacheProduct: 'products/cacheProduct',
  clearCachedProduct: 'products/clearCachedProduct',
  acceptDraftProduct: 'products/acceptDraftProduct',
  setProductBlocked: 'products/setProductBlocked',
  assignProductAllowedEditor: 'products/assignProductAllowedEditor'
} as const

/** Ignore out-of-order `getProducts` responses when filters change quickly. */
let getProductsRequestSeq = 0

export const getProducts = createAsyncThunk(
  types.getProducts,
  async (query: IProductQueryPayload, { rejectWithValue }) => {
    const requestSeq = ++getProductsRequestSeq

    try {
      const response = await ProductsRepository.getProducts({ query })

      return { ...response.data, requestSeq }
    } catch (err: any) {
      return rejectWithValue(err.response?.data ?? err.message)
    }
  }
)

/** Ignore out-of-order `getProduct` responses when switching products quickly. */
let getProductRequestSeq = 0

export const getProduct = createAsyncThunk(
  types.getProduct,
  async (id: string, { rejectWithValue }) => {
    const requestSeq = ++getProductRequestSeq
    const result = await executeWithTransientRetries(() =>
      ProductsRepository.getProduct(id).then(res => res.data)
    )

    if (result.ok) {
      return {
        product: result.data,
        requestSeq,
        requestedProductId: id
      }
    }

    return rejectWithValue(result.error)
  }
)

export const createProduct = createAsyncThunk(
  types.createProduct,
  async (payload: FormData, { rejectWithValue }) => {
    try {
      const response = await ProductsRepository.createProduct(payload)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const editProduct = createAsyncThunk(
  types.editProduct,
  async (payload: IEditProductPayload, { rejectWithValue }) => {
    const { id, formData } = payload

    const result = await executeWithTransientRetries(
      () => ProductsRepository.editProduct(id, formData).then(res => res.data),
      { maxAttempts: 5, baseDelayMs: 1000, retryForSave: true }
    )

    if (result.ok) {
      return result.data
    }

    return rejectWithValue(result.error)
  }
)

export const deleteProduct = createAsyncThunk(
  types.deleteProduct,
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await ProductsRepository.deleteProduct(id)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const generateSingleProductPackage = createAsyncThunk(
  types.generateSingleProductPackage,
  async (
    payload: IGenerateSingleProductPackagePayload,
    { rejectWithValue }
  ) => {
    try {
      const response = await ProductsRepository.generateSingleProductPackage(
        payload
      )

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const generateSingleProductIframe = createAsyncThunk(
  types.generateSingleProductIframe,
  async (
    payload: IGenerateSingleProductPackagePayload,
    { rejectWithValue }
  ) => {
    try {
      const response = await ProductsRepository.generateSingleProductIframe(
        payload
      )

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const generateMultipleProductPackage = createAsyncThunk(
  types.generateMultipleProductPackage,
  async (
    payload: IGenerateMultipleProductPackagePayload,
    { rejectWithValue }
  ) => {
    try {
      const response = await ProductsRepository.generateMultipleProductPackage(
        payload
      )

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const copyProducts = createAsyncThunk(
  types.copyProducts,
  async (payload: ICopyProductsPayload, { rejectWithValue }) => {
    try {
      const response = await ProductsRepository.copyProducts(payload)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const copySingleProduct = createAsyncThunk(
  types.copySingleProduct,
  async (payload: ICopySingleProductPayload, { rejectWithValue }) => {
    try {
      const response = await ProductsRepository.copySingleProduct(payload)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const autoTranslateSingleProduct = createAsyncThunk(
  types.autoTranslateSingleProduct,
  async (payload: IAutoTranslateSingleProductPayload, { rejectWithValue }) => {
    try {
      const response = await ProductsRepository.autoTranslateSingleProduct(
        payload
      )

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const acceptDraftProduct = createAsyncThunk(
  types.acceptDraftProduct,
  async (id: string, { rejectWithValue, getState }) => {
    try {
      const state = getState() as any
      const userId = state.auth.user?.id

      if (!userId) {
        return rejectWithValue('User ID not found')
      }

      const response = await ProductsRepository.acceptDraftProduct(id, userId)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const batchTranslateProducts = createAsyncThunk(
  'products/batchTranslateProducts',
  async (payload: IBatchTranslateRequest, { rejectWithValue }) => {
    try {
      const response = await ProductsRepository.batchTranslateProducts(payload)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const fetchBatchTranslationProgress = createAsyncThunk(
  'products/fetchBatchTranslationProgress',
  async (batchId: string, { rejectWithValue }) => {
    try {
      const response = await ProductsRepository.getBatchTranslationProgress(
        batchId
      )

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const cacheProduct = createAsyncThunk(
  types.cacheProduct,
  async (productId: string, { rejectWithValue }) => {
    try {
      const response = await ProductsRepository.getProduct(productId)

      return { productId, product: response.data as IEditProduct }
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const clearCachedProduct = createAsyncThunk(
  types.clearCachedProduct,
  async (productId: string) => {
    return productId
  }
)

export const setProductBlocked = createAsyncThunk(
  types.setProductBlocked,
  async (
    payload: { id: string; isBlocked: boolean },
    { rejectWithValue }
  ) => {
    try {
      const response = await ProductsRepository.setProductBlocked(
        payload.id,
        payload.isBlocked
      )

      return response.data as IEditProduct
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const assignProductAllowedEditor = createAsyncThunk(
  types.assignProductAllowedEditor,
  async (payload: { id: string; email: string }, { rejectWithValue }) => {
    try {
      const response = await ProductsRepository.assignProductAllowedEditor(
        payload.id,
        payload.email
      )

      return response.data as IEditProduct
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

/** Clears server-backed product snapshots (e.g. translation UI) so asset image changes cannot linger from persist/cache. */
export const clearAllCachedProducts = createAction(
  'products/clearAllCachedProducts'
)

export const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getProducts.pending, state => {
        if (state.products.length === 0) {
          state.listStatus = LoadingStatus.loading
        }
      })
      .addCase(getProducts.fulfilled, (state, action) => {
        if (action.payload.requestSeq !== getProductsRequestSeq) {
          return
        }
        state.listStatus = LoadingStatus.loaded
        state.products = action.payload.data
        state.pagination = action.payload.meta
      })
      .addCase(getProducts.rejected, (state, err) => {
        state.listStatus = LoadingStatus.failed
        handleApiErrors(err, false)
      })
      .addCase(getProduct.pending, state => {
        state.status = LoadingStatus.loading
      })
      .addCase(getProduct.fulfilled, (state, action) => {
        if (action.payload.requestSeq !== getProductRequestSeq) {
          return
        }
        state.status = LoadingStatus.loaded
        const product = action.payload.product as Record<string, unknown>
        const fromApiAssets =
          product?.assets && typeof product.assets === 'object'
            ? transformObjectKeys(product.assets as Record<string, unknown>)
            : {}
        const fromProductAssets = groupProductAssetsByAssetType(product)

        state.editedProduct = {
          ...product,
          assets: mergeGroupedProductAssets(fromProductAssets, fromApiAssets)
        } as IEditProduct
      })
      .addCase(getProduct.rejected, (state, err) => {
        state.status = LoadingStatus.failed
        handleApiErrors(err, false)
      })
      .addCase(createProduct.pending, state => {
        state.status = LoadingStatus.loading
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        state.products.push(action.payload)
      })
      .addCase(createProduct.rejected, (state, err) => {
        state.status = LoadingStatus.failed
        handleApiErrors(err, false)
      })
      .addCase(editProduct.pending, state => {
        state.status = LoadingStatus.loading
      })
      .addCase(editProduct.fulfilled, state => {
        state.status = LoadingStatus.loaded
      })
      .addCase(editProduct.rejected, (state, err) => {
        state.status = LoadingStatus.failed
        handleApiErrors(err, false)
      })
      .addCase(deleteProduct.rejected, (state, err) => {
        state.status = LoadingStatus.failed
        handleApiErrors(err, false)
      })
      .addCase(generateSingleProductPackage.pending, state => {
        state.status = LoadingStatus.loading
      })
      .addCase(generateSingleProductPackage.fulfilled, state => {
        state.status = LoadingStatus.loaded
      })
      .addCase(generateSingleProductPackage.rejected, (state, err) => {
        state.status = LoadingStatus.failed
        handleApiErrors(err, false)
      })
      .addCase(generateSingleProductIframe.pending, state => {
        state.status = LoadingStatus.loading
      })
      .addCase(generateSingleProductIframe.fulfilled, state => {
        state.status = LoadingStatus.loaded
      })
      .addCase(generateSingleProductIframe.rejected, (state, err) => {
        state.status = LoadingStatus.failed
        handleApiErrors(err, false)
      })
      .addCase(generateMultipleProductPackage.pending, state => {
        state.status = LoadingStatus.loading
      })
      .addCase(generateMultipleProductPackage.fulfilled, state => {
        state.status = LoadingStatus.loaded
      })
      .addCase(generateMultipleProductPackage.rejected, (state, err) => {
        state.status = LoadingStatus.failed
        handleApiErrors(err, false)
      })
      .addCase(copyProducts.pending, state => {
        state.status = LoadingStatus.loading
      })
      .addCase(copyProducts.fulfilled, state => {
        state.status = LoadingStatus.loaded
      })
      .addCase(copyProducts.rejected, (state, err) => {
        state.status = LoadingStatus.failed
        handleApiErrors(err, false)
      })
      .addCase(copySingleProduct.pending, state => {
        state.status = LoadingStatus.loading
      })
      .addCase(copySingleProduct.fulfilled, state => {
        state.status = LoadingStatus.loaded
      })
      .addCase(copySingleProduct.rejected, (state, err) => {
        state.status = LoadingStatus.failed
        handleApiErrors(err, false)
      })

      .addCase(autoTranslateSingleProduct.rejected, (state, err) => {
        state.status = LoadingStatus.failed
        handleApiErrors(err, false)
      })
      .addCase(acceptDraftProduct.pending, state => {
        state.status = LoadingStatus.loading
      })
      .addCase(acceptDraftProduct.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        if (state.editedProduct.id === action.payload.id) {
          ;(state.editedProduct as any).isDraft = false
        }
      })
      .addCase(acceptDraftProduct.rejected, (state, err) => {
        state.status = LoadingStatus.failed
        handleApiErrors(err, false)
      })

      .addCase(batchTranslateProducts.rejected, (state, err) => {
        state.status = LoadingStatus.failed
        handleApiErrors(err, false)
      })
      // Note: fetchBatchTranslationProgress is for polling and should not update global products status
      // to avoid triggering global loader during background polling
      .addCase(fetchBatchTranslationProgress.rejected, (state, err) => {
        // Don't update global status - this is just polling, not a user-initiated action
        handleApiErrors(err, false)
      })
      .addCase(cacheProduct.fulfilled, (state, action) => {
        const { productId, product } = action.payload

        if (!state.cachedProducts) {
          state.cachedProducts = {}
        }
        state.cachedProducts[productId] = product
      })
      .addCase(cacheProduct.rejected, (state, action) => {
        console.error(
          'cacheProduct.rejected: Failed to cache product:',
          action.payload
        )
      })
      .addCase(clearCachedProduct.fulfilled, (state, action) => {
        const productId = action.payload

        if (!state.cachedProducts) {
          state.cachedProducts = {}
        }
        delete state.cachedProducts[productId]
      })
      .addCase(clearAllCachedProducts, state => {
        state.cachedProducts = {}
      })
      .addCase(setProductBlocked.fulfilled, (state, action) => {
        const updated = action.payload

        if (updated?.id && state.editedProduct?.id === updated.id) {
          const product = updated as unknown as Record<string, unknown>
          const fromApiAssets =
            product.assets && typeof product.assets === 'object'
              ? transformObjectKeys(product.assets as Record<string, unknown>)
              : {}
          const fromProductAssets = groupProductAssetsByAssetType(product)

          // Keep existing form identity; only refresh lock + asset linkage from API.
          state.editedProduct = {
            ...state.editedProduct,
            ...updated,
            assets: mergeGroupedProductAssets(fromProductAssets, fromApiAssets)
          } as IEditProduct
        }
      })
      .addCase(setProductBlocked.rejected, (state, err) => {
        handleApiErrors(err, false)
      })
      .addCase(assignProductAllowedEditor.fulfilled, (state, action) => {
        const updated = action.payload

        if (updated?.id && state.editedProduct?.id === updated.id) {
          const product = updated as unknown as Record<string, unknown>
          const fromApiAssets =
            product.assets && typeof product.assets === 'object'
              ? transformObjectKeys(product.assets as Record<string, unknown>)
              : {}
          const fromProductAssets = groupProductAssetsByAssetType(product)

          state.editedProduct = {
            ...updated,
            assets: mergeGroupedProductAssets(fromProductAssets, fromApiAssets)
          } as IEditProduct
        }
      })
      .addCase(assignProductAllowedEditor.rejected, (state, err) => {
        handleApiErrors(err, false)
      })
  }
})

export const selectProducts = (state: RootState): IProductsState =>
  state.products

export const selectProductsStatus: (state: RootState) => TLoadingStatus =
  createSelector(selectProducts, productsState => productsState.status)

export const selectProductsListStatus: (state: RootState) => TLoadingStatus =
  createSelector(
    selectProducts,
    productsState => productsState.listStatus ?? LoadingStatus.initial
  )
export const selectProductsPagination: (state: RootState) => IPagination =
  createSelector(
    selectProducts,
    pathOr(
      {
        page: 0,
        pagesTotal: 0,
        recordsTotal: 0,
        take: 0
      },
      ['pagination']
    )
  )

export const selectGetProducts: (state: RootState) => IProduct[] =
  createSelector(selectProducts, pathOr([], ['products']))

export const selectGetEditedProduct: (state: RootState) => IEditProduct =
  createSelector(selectProducts, productsState => productsState.editedProduct)

export const selectCachedProducts: (
  state: RootState
) => Record<string, IEditProduct> = createSelector(
  selectProducts,
  productsState => productsState.cachedProducts || {}
)

export const selectCachedProduct =
  (productId: string) => (state: RootState) => {
    const products = state.products as IProductsState | undefined

    if (!products?.cachedProducts) {
      return null
    }

    return products.cachedProducts[productId] || null
  }

export default productsSlice.reducer
